/**
 * GET ON THE TV — the page's own rules and words (TV-PRO-1). The door's answer comes from the Owner Hub's /pro/tv
 * (image-os-web routes/pro.tv.ts; shapes in image-os-web lib/tv-pro-submit.ts, ProTvPayload / SubmissionView). The door
 * decides; this file only lets the page say "no" a moment sooner than the door would, in the same words, and never
 * says "yes" to something the door would refuse.
 */

export type PieceState = 'waiting' | 'playing' | 'approved' | 'ended' | 'sent_back' | 'declined';

export interface PieceView {
  id: string;
  kind: 'photo' | 'video' | 'instagram' | 'promotion';
  mediaUrl: string | null;
  mediaKind: 'image' | 'video';
  caption: string | null;
  instagramPermalink: string | null;
  durationMs: number | null;
  requestedEndsAt: string | null;
  status: 'submitted' | 'approved' | 'sent_back' | 'declined' | 'expired';
  state: PieceState;
  note: string | null;
  startsAt: string | null;
  endsAt: string | null;
  createdAt: string;
  decidedAt: string | null;
  /** The TVs it is on right now — empty unless `state` is 'playing'. */
  playingOn: string[];
}

export interface TvPayload {
  ok: true;
  on: boolean;
  studioName?: string | null;
  guidelines?: string;
  maxLivePerPro?: number;
  runDays?: number;
  submissions?: PieceView[];
  instagram?: { externalId: string; imageUrl: string; permalink: string | null; caption: string | null }[];
  viewing?: boolean;
}

/** The pro's words for each state — the same words the door's owner sees mirrored (image-os-web PRO_STATE_WORDS). */
export const STATE_WORDS: Record<PieceState, string> = {
  waiting: 'Waiting for the owner',
  playing: 'Playing now',
  approved: 'Approved. Not on a screen right now',
  ended: 'Ended',
  sent_back: 'Sent back',
  declined: 'Not this time',
};

export const CAPTION_MAX = 80;
export const MAX_VIDEO_MS = 30_000;
export const MAX_PHOTO_BYTES = 10 * 1024 * 1024;
export const MAX_VIDEO_BYTES = 50 * 1024 * 1024;
/** A TV is wide: a video narrower than 4:3 cannot play on one (the TV's own rule, image-os-web tv/video-shape.ts). */
export const MIN_TV_ASPECT = 1.3;
export const MAX_WAITING = 5;

/**
 * WHAT IS WRONG WITH THE PICKED FILE, in the door's own words, or null when the page has nothing to object to (the door
 * still checks everything again — a video's shape from its own bytes).
 */
export function checkPick(f: { type: string; size: number; video: { ms: number; width: number; height: number } | null } | null): string | null {
  if (!f) return null;
  const isVideo = f.type.startsWith('video/');
  if (!isVideo && !f.type.startsWith('image/')) return 'Send a photo or a video.';
  if (!isVideo && f.size > MAX_PHOTO_BYTES) return 'That photo is larger than 10MB — try a smaller one.';
  if (isVideo && f.size > MAX_VIDEO_BYTES) return 'That video is larger than 50MB — send a shorter one.';
  if (isVideo && f.video) {
    const { width: w, height: h, ms } = f.video;
    if (w > 0 && h > 0 && h > w) return 'This video is tall (made for a phone). TV screens are wide, so it can’t play here. Use a landscape video.';
    if (w > 0 && h > 0 && w / h < MIN_TV_ASPECT) return 'This video is square, or close to it. TV screens are wide, so it can’t play here. Use a landscape video.';
    if (ms > MAX_VIDEO_MS + 500) return 'Keep the video to 30 seconds or less.';
  }
  return null;
}

/** Ready to send: something picked (a file or an Instagram post), nothing wrong with it, and room in the queue. */
export function canSend(s: { file: boolean; instagram: boolean; problem: string | null; waiting: number }): boolean {
  return (s.file || s.instagram) && !s.problem && s.waiting < MAX_WAITING;
}

const ymd = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/**
 * "Stop showing it": when the owner's run ends (no date — the default), or sooner. Only choices shorter than the run are
 * offered, since a later one would not change anything.
 */
export function endChoices(now: Date, runDays: number): { label: string; date: string }[] {
  const out = [{ label: `After ${runDays} days`, date: '' }];
  for (const [label, n] of [['In 3 days', 3], ['In a week', 7]] as const) {
    if (n < runDays) out.push({ label, date: ymd(new Date(now.getFullYear(), now.getMonth(), now.getDate() + n)) });
  }
  return out;
}
